import { clearStoredToken, getStoredToken, getChildToken, clearChildToken } from './auth.js'

const BASE = '/api'

async function req(method, path, body, extraHeaders = {}) {
  const headers = { ...extraHeaders }
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  const token = getChildToken() || getStoredToken()
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal: AbortSignal.timeout(10000),
  })

  if (res.status === 401) {
    clearStoredToken()
    clearChildToken()
    window.location.href = '/login'
    throw new Error('Session expired')
  }

  const contentType = res.headers.get('content-type') || ''
  const isJson = contentType.includes('application/json')
  const data = isJson ? await res.json() : await res.text()

  if (!res.ok) {
    const message = typeof data === 'string' ? data : data?.error
    throw new Error(message || 'Request failed')
  }

  return data
}

const get = (path) => req('GET', path)
const post = (path, body) => req('POST', path, body)

export const api = {
  auth: {
    register: (email, password, display_name, inviteCode) =>
      post('/auth/register', { email, password, display_name, inviteCode: inviteCode || undefined }),
    login: (email, password) =>
      post('/auth/login', { email, password }),
    passwordResetStatus: () => get('/auth/password-reset-status'),
    forgotPassword: (email) => post('/auth/forgot-password', { email }),
    validateResetToken: (token) => get(`/auth/reset-password?token=${encodeURIComponent(token)}`),
    resetPassword: (token, password) => post('/auth/reset-password', { token, password }),
    verifyEmail: (token) => get(`/auth/verify-email?token=${encodeURIComponent(token)}`),
    resendVerification: (email) => post('/auth/resend-verification', { email }),
    demo: () => post('/auth/demo', {}),
  },
   family: {
     get: () => get('/family'),
     streak: () => get('/family/streak'),
     completeOnboarding: () => post('/family/complete-onboarding', {}),
     addChild: (name, birth_year, avatar_color, birth_month, gender) =>
      post('/family/children', { name, birth_year, birth_month, avatar_color, gender }),
    updateChild: (id, data) => req('PATCH', `/family/children/${id}`, data),
    deleteChild: (id) => req('DELETE', `/family/children/${id}`),
    deleteChildPhoto: (id) => req('DELETE', `/family/children/${id}/photo`),
    setChildAvatar: (id, avatarUrl) => req('PATCH', `/family/children/${id}/avatar`, { avatar_url: avatarUrl }),
    deleteFamilyPhoto: () => req('DELETE', '/family/photo'),
    deleteAccount: () => req('DELETE', '/family/account'),
    exportData: () => get('/family/export'),
     uploadChildPhoto: async (id, file) => {
       const fd = new FormData()
       fd.append('photo', file)
       const token = getChildToken() || getStoredToken()
       const res = await fetch(`/api/family/children/${id}/photo`, {
         method: 'POST',
         headers: token ? { Authorization: 'Bearer ' + token } : {},
         body: fd,
         signal: AbortSignal.timeout(30000),
       })
       if (res.status === 401) { clearStoredToken(); clearChildToken(); window.location.href = '/login'; return }
       const ct = res.headers.get('content-type') || ''
       const data = ct.includes('application/json') ? await res.json() : await res.text()
       if (!res.ok) throw new Error((typeof data === 'string' ? data : data?.error) || 'Upload failed')
       return data
     },
    updateProfile: (data) => req('PATCH', '/family/profile', data),
     updateProfilePhoto: async (file) => {
       const fd = new FormData()
       fd.append('photo', file)
       const token = getChildToken() || getStoredToken()
       const res = await fetch('/api/family/profile/photo', {
         method: 'POST',
         headers: token ? { Authorization: 'Bearer ' + token } : {},
         body: fd,
         signal: AbortSignal.timeout(30000),
       })
       if (res.status === 401) { clearStoredToken(); clearChildToken(); window.location.href = '/login'; return }
       const data = await res.json()
       if (!res.ok) throw new Error(data?.error || 'Upload failed')
       return data
     },
    setProfileAvatar: (avatarUrl) => req('PATCH', '/family/profile/avatar', { avatar_url: avatarUrl }),
    deleteProfilePhoto: () => req('DELETE', '/family/profile/photo'),
    updateFamily: (data) => req('PATCH', '/family', data),
    updateTheme: (quest_theme) => req('PATCH', '/family/theme', { quest_theme }),
    updateShareDefault: (share_default) => req('PATCH', '/family/share-default', { share_default }),
     updateFamilyPhoto: async (file) => {
       const fd = new FormData()
       fd.append('photo', file)
       const token = getChildToken() || getStoredToken()
       const res = await fetch('/api/family/photo', {
         method: 'POST',
         headers: token ? { Authorization: 'Bearer ' + token } : {},
         body: fd,
         signal: AbortSignal.timeout(30000),
       })
       if (res.status === 401) { clearStoredToken(); clearChildToken(); window.location.href = '/login'; return }
       const data = await res.json()
       if (!res.ok) throw new Error(data?.error || 'Upload failed')
       return data
     },
    createInvite: () => post('/family/invite', {}),
    getInvite: (code) => get(`/family/invite/${code.toUpperCase()}`),
    joinViaInvite: (code) => post(`/family/invite/${code.toUpperCase()}/join`, {}),
     validateInvite: (code) => get(`/family/invite/${code.toUpperCase()}`),
     childProgress: (id) => get(`/family/children/${id}/progress`),
     siblingLeague: () => get('/family/children/weekly-league'),
   },
   quests: {
     list: (category) => get(`/quests${category && category !== 'all' ? `?category=${category}` : ''}`),
     familyQuests: () => get('/family-quests'),
     createFamilyQuest: (data) => post('/family-quests', data),
     updateFamilyQuest: (id, data) => req('PATCH', `/family-quests/${id}`, data),
     deleteFamilyQuest: (id) => req('DELETE', `/family-quests/${id}`),
      complete: async (id, childId, notes, proofs, shareWithGroup, collaboratingFamilyId) => {
       const proofList = Array.isArray(proofs) ? proofs : (proofs ? [proofs] : [])
       if (proofList.length > 0) {
         const fd = new FormData()
         if (childId) fd.append('childId', childId)
         if (notes) fd.append('notes', notes)
         fd.append('shareWithGroup', shareWithGroup ? 'true' : 'false')
         if (collaboratingFamilyId) fd.append('collaborating_family_id', collaboratingFamilyId)
         for (const file of proofList) fd.append('proof', file)
         const token = getChildToken() || getStoredToken()
         const res = await fetch('/api/quests/' + id + '/complete', {
           method: 'POST',
           headers: token ? { Authorization: 'Bearer ' + token } : {},
           body: fd,
           signal: AbortSignal.timeout(90000),
         })
         if (res.status === 401) { clearStoredToken(); clearChildToken(); window.location.href = '/login'; return }
         const ct = res.headers.get('content-type') || ''
         const data = ct.includes('application/json') ? await res.json() : await res.text()
         if (!res.ok) throw new Error((typeof data === 'string' ? data : data?.error) || 'Request failed')
         return data
       }
       return req('POST', `/quests/${id}/complete`, { childId, notes, shareWithGroup: false, collaborating_family_id: collaboratingFamilyId || null })
     },
     completions: () => get('/quests/completions'),
     pendingCollabInvites: () => get('/quests/completions/pending-collab'),
     approve: (id, bonusXp = 0) => post(`/quests/completions/${id}/approve`, { bonusXp }),
    deleteCompletion: (id) => req('DELETE', `/quests/completions/${id}`),
    editCompletion: (id, notes, childId) => req('PATCH', `/quests/completions/${id}`, { notes, childId }),
    revokeApproval: (id) => req('POST', `/quests/completions/${id}/revoke`, {}),
    rejectCompletion: (id, reason) => req('POST', `/quests/completions/${id}/reject`, { reason: reason || null }),
    toggleShare: (completionId, share) => req('PATCH', `/quests/completions/${completionId}/share`, { share }),
    shareAll: (share) => req('PATCH', '/quests/completions/share-all', { share }),
    hideQuest: (questId) => req('POST', `/quests/${questId}/hide`),
    unhideQuest: (questId) => req('DELETE', `/quests/${questId}/hide`),
    groupFeed: (groupId) => get(`/quests/feed/${groupId}`),
    allGroupsFeed: () => get('/quests/feed'),
    activeQuest: () => get('/quests/active'),
    activateQuest: (id, childId) => post(`/quests/${id}/activate`, { childId: childId || null }),
    deactivateQuest: () => req('DELETE', '/quests/active'),
    dailyQuests: () => get('/quests/daily'),
  },
  groups: {
    create: (name) => post('/groups', { name }),
    join: (invite_code, shareExistingMedia = false) => post('/groups/join', { invite_code, shareExistingMedia }),
    invite: (code) => get(`/groups/invite/${code.toUpperCase()}`),
    mine: () => get('/groups/me'),
    leaderboard: (id) => get(`/groups/${id}/leaderboard`),
    weeklyChallenge: (id) => get(`/groups/${id}/weekly-challenge`),
    leave: (id) => req('DELETE', `/groups/${id}/leave`),
  },
  goals: {
    list: () => get('/goals'),
    create: (data) => post('/goals', data),
    update: (id, data) => req('PATCH', `/goals/${id}`, data),
    redeem: (id) => req('PATCH', `/goals/${id}/redeem`, {}),
    delete: (id) => req('DELETE', `/goals/${id}`),
  },
  chat: {
    messages: (groupId) => get(`/groups/${groupId}/messages`),
    send: (groupId, message, chatActive = false) => req('POST', `/groups/${groupId}/messages`, { message }, chatActive ? { 'x-chat-active': '1' } : {}),
    editMessage: (groupId, msgId, message) => req('PATCH', `/groups/${groupId}/messages/${msgId}`, { message }),
    deleteMessage: (groupId, msgId) => req('DELETE', `/groups/${groupId}/messages/${msgId}`),
  },
  proposals: {
    list: () => get('/quest-proposals'),
    submit: (data) => post('/quest-proposals', data),
    vote: (id) => post(`/quest-proposals/${id}/votes`, {}),
    unvote: (id) => req('DELETE', `/quest-proposals/${id}/votes`),
    adminList: () => get('/quest-proposals/admin'),
    approve: (id, overrides) => post(`/quest-proposals/${id}/approve`, overrides || {}),
    reject: (id, note) => post(`/quest-proposals/${id}/reject`, { review_note: note }),
  },
  adminQuests: {
    list: () => get('/admin/quests'),
    create: (data) => post('/admin/quests', data),
    update: (id, data) => req('PATCH', `/admin/quests/${id}`, data),
    toggle: (id) => req('PATCH', `/admin/quests/${id}/toggle`, {}),
    aiSuggest: (opts) => post('/admin/quests/ai-suggest', opts),
    import: (data) => post('/admin/quests/import', data),
  },
  push: {
    getVapidKey: () => get('/push/vapid-public-key'),
    subscribe: (subscription) => req('POST', '/push/subscribe', {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
        auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')))),
      },
    }),
    unsubscribe: (endpoint) => req('DELETE', '/push/subscribe', { endpoint }),
  },
  notifications: {
    count: () => get('/notifications/count'),
    list: () => get('/notifications'),
    markAllRead: () => req('PATCH', '/notifications/read-all'),
    markRead: (id) => req('PATCH', `/notifications/${id}/read`),
    markChatRead: (groupId) => req('PATCH', `/notifications/read-chat/${groupId}`),
  },
  stats: {
    admin: (period = '30d') => get(`/stats/admin?period=${period}`),
    public: () => get('/stats/public'),
  },
   ai: {
     stats: () => get('/ai/stats'),
     recentJobs: (limit = 20) => get(`/ai/jobs/recent?limit=${limit}`),
     generateThemes: () => post('/ai/generate-themes', {}),
     regenerateTheme: (theme) => post(`/ai/regenerate-theme/${theme}`, {}),
     cancelThemes: () => post('/ai/cancel-themes', {}),
     truncateThemes: () => post('/ai/truncate-themes', { confirm: 'DELETE ALL THEMES' }),
     themeJobStats: () => get('/ai/theme-jobs/stats'),
     recentThemeJobs: (limit = 50) => get(`/ai/theme-jobs/recent?limit=${limit}`),
     failedThemeJobs: () => get('/ai/theme-jobs/failed'),
     retryThemeJob: (id) => post(`/ai/theme-jobs/${id}/retry`, {}),
     workers: () => get('/ai/workers'),
     themeSettings: () => get('/ai/theme-settings'),
     saveThemeSettings: (data) => post('/ai/theme-settings', data),
     visionSettings: () => get('/ai/vision-settings'),
     saveVisionSettings: (data) => post('/ai/vision-settings', data),
      ollamaModels: () => get('/ai/ollama-models'),
      pullModel: (model) => post('/ai/ollama-models/pull', { model }),
      deleteOllamaModel: (modelName) => req('DELETE', `/ai/ollama-models/${encodeURIComponent(modelName)}`),
      getPullProgress: () => get('/ai/pull-progress'),
      ollamaSettings: () => get('/ai/ollama-settings'),
      saveOllamaSettings: (body) => post('/ai/ollama-settings', body),
      unloadModel: (model) => post('/ai/ollama-models/unload', { model }),
    },
  version: {
    info: () => get('/version'),
    changelog: () => get('/version/changelog'),
  },
  settings: {
    get: () => get('/settings'),
    update: (patch) => req('PATCH', '/settings', patch),
    publicSettings: () => get('/settings/public'),
  },
  feedback: {
    submit: (data) => post('/feedback', data),
    adminList: (params = {}) => {
      const q = new URLSearchParams(params).toString()
      return get(`/feedback/admin${q ? '?' + q : ''}`)
    },
    adminUnreadCount: () => get('/feedback/admin/unread-count'),
    adminUpdate: (id, data) => req('PATCH', `/feedback/admin/${id}`, data),
  },
   childDevice: {
      getFamily: () => get('/child-device/family'),
      generateToken: () => post('/child-device/token', {}),
      generateChildToken: (childId) => req('POST', `/child-device/token/${childId}`),
    },
   admin: {
     getMailErrors: () => get('/admin/mail-errors'),
     resolveMailError: (id) => req('POST', `/admin/mail-errors/${id}/resolve`, {}),
   },
 }
